import React, { useState, useRef, useEffect } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { usePacientes } from '../hooks/usePacientes';
import { useAuth } from '../contexts/AuthContext';
import { Paciente } from '../services/mockData';
import { sendPDFToPatient, generatePDFBlob } from '../services/pdfSendService';
import { PDFSendModal } from '../components/pdf/PDFSendModal';
import { database } from '../config/backend';
import { ref, get as rtdbGet, push, set as rtdbSet, query, orderByChild, limitToLast } from '../services/phpDataAdapter';
import './Pages.css';

const EXAM_TEMPLATES = [
    {
        id: 'basico',
        name: 'Exames Básicos',
        description: 'Hemograma, glicemia e perfil lipídico',
        exams: ['hemograma', 'glicemia', 'colesterol']
    },
    {
        id: 'vitaminas',
        name: 'Perfil de Vitaminas',
        description: 'Vitamina D, B12 e ácido fólico',
        exams: ['vitamina_d', 'vitamina_b12', 'acido_folico']
    },
    {
        id: 'tireoide',
        name: 'Perfil Tireoidiano',
        description: 'TSH e T4 Livre',
        exams: ['tsh', 't4livre']
    },
    {
        id: 'ferro',
        name: 'Perfil de Ferro',
        description: 'Ferro sérico e ferritina',
        exams: ['ferro', 'ferritina']
    },
    {
        id: 'renal',
        name: 'Função Renal',
        description: 'Creatinina e ureia',
        exams: ['creatinina', 'ureia']
    },
    {
        id: 'hepatico',
        name: 'Função Hepática',
        description: 'TGO e TGP',
        exams: ['tgo', 'tgp']
    },
    {
        id: 'completo',
        name: 'Exames Completos',
        description: 'Todos os exames principais',
        exams: ['hemograma', 'glicemia', 'colesterol', 'tsh', 't4livre', 'vitamina_d', 'vitamina_b12', 'ferro', 'ferritina', 'creatinina', 'tgo', 'tgp', 'pcr']
    }
];

const EXAM_TYPES = [
    { id: 'hemograma', label: 'Hemograma Completo', category: 'Sangue' },
    { id: 'glicemia', label: 'Glicemia de Jejum', category: 'Sangue' },
    { id: 'colesterol', label: 'Perfil Lipídico (Colesterol Total, HDL, LDL, Triglicérides)', category: 'Sangue' },
    { id: 'tsh', label: 'TSH (Hormônio Tireoestimulante)', category: 'Hormônios' },
    { id: 't4livre', label: 'T4 Livre', category: 'Hormônios' },
    { id: 'vitamina_d', label: 'Vitamina D (25-OH)', category: 'Vitaminas' },
    { id: 'vitamina_b12', label: 'Vitamina B12', category: 'Vitaminas' },
    { id: 'acido_folico', label: 'Ácido Fólico', category: 'Vitaminas' },
    { id: 'ferro', label: 'Ferro Sérico', category: 'Minerais' },
    { id: 'ferritina', label: 'Ferritina', category: 'Minerais' },
    { id: 'calcio', label: 'Cálcio', category: 'Minerais' },
    { id: 'magnesio', label: 'Magnésio', category: 'Minerais' },
    { id: 'creatinina', label: 'Creatinina', category: 'Função Renal' },
    { id: 'ureia', label: 'Ureia', category: 'Função Renal' },
    { id: 'tgo', label: 'TGO (AST)', category: 'Função Hepática' },
    { id: 'tgp', label: 'TGP (ALT)', category: 'Função Hepática' },
    { id: 'pcr', label: 'PCR (Proteína C Reativa)', category: 'Inflamação' },
    { id: 'eas', label: 'EAS (Exame de Urina)', category: 'Urina' },
    { id: 'parasitologico', label: 'Parasitológico de Fezes', category: 'Fezes' },
];

export const ExamRequests: React.FC = () => {
    const { user } = useAuth();
    const { pacientes } = usePacientes();
    const signatureCanvasRef = useRef<HTMLCanvasElement>(null);

    const [selectedPatient, setSelectedPatient] = useState<string>('');
    const [selectedExams, setSelectedExams] = useState<string[]>([]);
    const [additionalNotes, setAdditionalNotes] = useState<string>('');
    const [isDrawing, setIsDrawing] = useState<boolean>(false);
    const [showPreview, setShowPreview] = useState<boolean>(false);
    const [isPDFSendModalOpen, setIsPDFSendModalOpen] = useState(false);
    const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
    const [selectedTemplate, setSelectedTemplate] = useState<string>('');
    const [examHistory, setExamHistory] = useState<any[]>([]);
    const [showHistory, setShowHistory] = useState(false);
    const [nutritionistCRM, setNutritionistCRM] = useState<string>('');
    const [nutritionistName, setNutritionistName] = useState<string>('');
    const [watermarkText, setWatermarkText] = useState<string>('');

    useEffect(() => {
        if (user) {
            loadExamHistory();
            loadNutritionistData();
        }
    }, [user]);

    const loadNutritionistData = async () => {
        if (!user?.uid) return;
        try {
            const userRef = ref(database, `users/${user.uid}`);
            const snapshot = await rtdbGet(userRef);
            if (snapshot.exists()) {
                const userData = snapshot.val();
                // Buscar CRN em múltiplos campos possíveis
                const crn = userData.crn || userData.crm || userData.CRN || userData.CRM || '';
                setNutritionistCRM(crn);
                // Buscar nome do nutricionista
                const name = userData.name || userData.nome || user.displayName || user.email?.split('@')[0] || 'Nutricionista';
                setNutritionistName(name);
                // Buscar marca d'água personalizada
                const watermark = userData.watermark || userData.marcaAgua || '';
                setWatermarkText(watermark);
            } else {
                // Se não encontrar no banco, usar dados do servidor PHP/MySQL Auth
                setNutritionistName(user.displayName || user.email?.split('@')[0] || 'Nutricionista');
            }
        } catch (error) {
            console.error('Erro ao carregar dados do nutricionista:', error);
            // Fallback para dados do servidor PHP/MySQL Auth
            setNutritionistName(user?.displayName || user?.email?.split('@')[0] || 'Nutricionista');
        }
    };

    const saveWatermark = async (text: string) => {
        if (!user?.uid) return;
        try {
            await rtdbSet(ref(database, `users/${user.uid}/watermark`), text);
            setWatermarkText(text);
            alert('Marca d\'água salva com sucesso!');
        } catch (error) {
            console.error('Erro ao salvar marca d\'água:', error);
            alert('Erro ao salvar marca d\'água. Tente novamente.');
        }
    };

    useEffect(() => {
        if (selectedTemplate) {
            const template = EXAM_TEMPLATES.find(t => t.id === selectedTemplate);
            if (template) {
                setSelectedExams(template.exams);
            }
        }
    }, [selectedTemplate]);

    const loadExamHistory = async () => {
        if (!user?.uid) return;
        try {
            const historyRef = ref(database, `examRequests/${user.uid}`);
            const snapshot = await rtdbGet(query(historyRef, orderByChild('createdAt'), limitToLast(20)));
            if (snapshot.exists()) {
                const data = snapshot.val();
                const history = Object.entries(data).map(([id, request]: [string, any]) => ({
                    id,
                    ...request
                })).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
                setExamHistory(history);
            }
        } catch (error) {
            console.error('Erro ao carregar histórico:', error);
        }
    };

    const saveToHistory = async (patientId: string, patientName: string, exams: string[], notes: string) => {
        if (!user?.uid) return;
        try {
            const historyRef = push(ref(database, `examRequests/${user.uid}`));
            await rtdbSet(historyRef, {
                patientId,
                patientName,
                exams,
                notes,
                createdAt: new Date().toISOString()
            });
            await loadExamHistory();
        } catch (error) {
            console.error('Erro ao salvar histórico:', error);
        }
    };

    const loadFromHistory = (historyItem: any) => {
        setSelectedPatient(historyItem.patientId);
        setSelectedExams(historyItem.exams);
        setAdditionalNotes(historyItem.notes || '');
        setShowHistory(false);
    };

    useEffect(() => {
        const canvas = signatureCanvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
    }, []);

    const handleExamToggle = (examId: string) => {
        setSelectedExams(prev =>
            prev.includes(examId)
                ? prev.filter(id => id !== examId)
                : [...prev, examId]
        );
    };

    const handleCategoryToggle = (category: string) => {
        const categoryExams = EXAM_TYPES.filter(e => e.category === category).map(e => e.id);
        const allSelected = categoryExams.every(id => selectedExams.includes(id));

        if (allSelected) {
            setSelectedExams(prev => prev.filter(id => !categoryExams.includes(id)));
        } else {
            const uniqueExams = Array.from(new Set([...selectedExams, ...categoryExams]));
            setSelectedExams(uniqueExams);
        }
    };

    const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
        const canvas = signatureCanvasRef.current;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.beginPath();
        ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
        setIsDrawing(true);
    };

    const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!isDrawing) return;

        const canvas = signatureCanvasRef.current;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    };

    const stopDrawing = () => {
        setIsDrawing(false);
    };

    const clearSignature = () => {
        const canvas = signatureCanvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    const handlePrint = () => {
        if (!selectedPatient || selectedExams.length === 0) {
            alert('Selecione um paciente e pelo menos um exame primeiro');
            return;
        }
        
        setShowPreview(true);
        // Aguardar um pouco mais para garantir que o DOM seja atualizado
        setTimeout(() => {
            window.print();
            // Ocultar preview após impressão
            setTimeout(() => {
                setShowPreview(false);
            }, 500);
        }, 200);
    };

    const handleSendPDF = async () => {
        if (!user?.uid || !selectedPatient) {
            alert('Selecione um paciente primeiro');
            return;
        }

        try {
            // Gerar PDF do pedido de exame
            const jsPDF = (await import('jspdf')).default;
            const doc = new jsPDF();
            
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 15; // Reduzido de 20 para 15
            const contentWidth = pageWidth - (margin * 2);
            
            // Função para adicionar marca d'água em todas as páginas
            const addWatermark = (pageNum: number) => {
                if (watermarkText && watermarkText.trim()) {
                    doc.setPage(pageNum);
                    // Usar cor cinza clara para simular opacidade
                    doc.setTextColor(220, 220, 220); // Cinza muito claro
                    doc.setFontSize(45); // Tamanho grande para marca d'água
                    doc.setFont('helvetica', 'bold');
                    const textWidth = doc.getTextWidth(watermarkText);
                    const x = (pageWidth - textWidth) / 2;
                    const y = pageHeight / 2;
                    // Tentar adicionar texto rotacionado (jsPDF pode não suportar angle diretamente)
                    try {
                        doc.text(watermarkText, x, y, {
                            angle: 45,
                            align: 'center'
                        });
                    } catch (e) {
                        // Fallback: texto sem rotação se angle não for suportado
                        doc.text(watermarkText, x, y, { align: 'center' });
                    }
                }
            };
            
            let yPos = margin + 5; // Reduzido de 10 para 5
            
            // Cabeçalho profissional
            doc.setDrawColor(42, 157, 143);
            doc.setLineWidth(0.8); // Reduzido de 1 para 0.8
            doc.line(margin, yPos, pageWidth - margin, yPos);
            yPos += 5; // Reduzido de 8 para 5
            
            doc.setFontSize(18); // Reduzido de 20 para 18
            doc.setTextColor(15, 23, 42);
            doc.setFont('helvetica', 'bold');
            doc.text('PEDIDO DE EXAMES LABORATORIAIS', pageWidth / 2, yPos, { align: 'center' });
            yPos += 5; // Reduzido de 8 para 5
            
            doc.setDrawColor(42, 157, 143);
            doc.setLineWidth(0.8);
            doc.line(margin, yPos, pageWidth - margin, yPos);
            yPos += 12; // Reduzido de 18 para 12
            
            // Informações do paciente
            const patientName = getPatientName(selectedPatient);
            const currentDate = new Date().toLocaleDateString('pt-BR', { 
                day: '2-digit', 
                month: '2-digit', 
                year: 'numeric' 
            });
            
            doc.setFontSize(10); // Reduzido de 11 para 10
            doc.setTextColor(15, 23, 42);
            doc.setFont('helvetica', 'bold');
            doc.text('Paciente:', margin, yPos);
            
            doc.setFont('helvetica', 'normal');
            doc.text(patientName, margin + 25, yPos); // Reduzido de 30 para 25
            
            yPos += 5; // Reduzido de 7 para 5
            
            doc.setFont('helvetica', 'bold');
            doc.text('Data:', margin, yPos);
            
            doc.setFont('helvetica', 'normal');
            doc.text(currentDate, margin + 25, yPos);
            
            yPos += 10; // Reduzido de 15 para 10
            
            // Linha divisória
            doc.setDrawColor(226, 232, 240);
            doc.setLineWidth(0.3); // Reduzido de 0.5 para 0.3
            doc.line(margin, yPos, pageWidth - margin, yPos);
            yPos += 8; // Reduzido de 12 para 8
            
            // Título da seção de exames
            doc.setFontSize(12); // Reduzido de 13 para 12
            doc.setTextColor(42, 157, 143);
            doc.setFont('helvetica', 'bold');
            doc.text('EXAMES SOLICITADOS', margin, yPos);
            yPos += 6; // Reduzido de 10 para 6
            
            // Linha divisória
            doc.setDrawColor(226, 232, 240);
            doc.setLineWidth(0.3);
            doc.line(margin, yPos, pageWidth - margin, yPos);
            yPos += 5; // Reduzido de 8 para 5
            
            // Lista de exames agrupados por categoria
            const examsByCategory: Record<string, string[]> = {};
            selectedExams.forEach(examId => {
                const exam = EXAM_TYPES.find(e => e.id === examId);
                if (exam) {
                    if (!examsByCategory[exam.category]) {
                        examsByCategory[exam.category] = [];
                    }
                    examsByCategory[exam.category].push(exam.label);
                }
            });
            
            doc.setFontSize(9); // Reduzido de 10 para 9
            doc.setTextColor(15, 23, 42);
            doc.setFont('helvetica', 'normal');
            
            Object.entries(examsByCategory).forEach(([category, exams]) => {
                // Verificar se precisa de nova página
                if (yPos > pageHeight - 50) { // Reduzido de 70 para 50
                    doc.addPage();
                    yPos = margin + 5; // Reduzido de 10 para 5
                }
                
                // Nome da categoria
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(100, 116, 139);
                doc.setFontSize(9); // Reduzido de 10 para 9
                doc.text(category.toUpperCase() + ':', margin, yPos);
                yPos += 5; // Reduzido de 7 para 5
                
                // Exames da categoria
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(15, 23, 42);
                doc.setFontSize(9);
                
                exams.forEach(examLabel => {
                    if (yPos > pageHeight - 50) { // Reduzido de 70 para 50
                        doc.addPage();
                        yPos = margin + 5;
                    }
                    
                    // Bullet point e texto do exame
                    doc.text('•', margin + 2, yPos);
                    const examLines = doc.splitTextToSize(examLabel, contentWidth - 8); // Reduzido de 10 para 8
                    doc.text(examLines, margin + 6, yPos); // Reduzido de 7 para 6
                    yPos += examLines.length * 4 + 1; // Reduzido de 5 para 4 e 2 para 1
                });
                
                yPos += 2; // Reduzido de 4 para 2 - Espaço entre categorias
            });

            // Observações (apenas se houver)
            if (additionalNotes && additionalNotes.trim()) {
                yPos += 5; // Reduzido de 8 para 5
                
                if (yPos > pageHeight - 45) { // Reduzido de 60 para 45
                    doc.addPage();
                    yPos = margin + 5;
                }
                
                // Título da seção de observações
                doc.setFontSize(12); // Reduzido de 13 para 12
                doc.setTextColor(42, 157, 143);
                doc.setFont('helvetica', 'bold');
                doc.text('OBSERVAÇÕES', margin, yPos);
                yPos += 6; // Reduzido de 10 para 6
                
                // Linha divisória
                doc.setDrawColor(226, 232, 240);
                doc.setLineWidth(0.3); // Reduzido de 0.5 para 0.3
                doc.line(margin, yPos, pageWidth - margin, yPos);
                yPos += 5; // Reduzido de 8 para 5
                
                // Texto das observações
                doc.setFontSize(9); // Reduzido de 10 para 9
                doc.setTextColor(15, 23, 42);
                doc.setFont('helvetica', 'normal');
                const splitNotes = doc.splitTextToSize(additionalNotes.trim(), contentWidth - 5);
                
                splitNotes.forEach((line: string) => {
                    if (yPos > pageHeight - 45) { // Reduzido de 60 para 45
                        doc.addPage();
                        yPos = margin + 5;
                    }
                    doc.text(line, margin, yPos);
                    yPos += 4; // Reduzido de 5 para 4
                });
            }

            // Assinatura - sempre na última página
            if (yPos > pageHeight - 40) { // Reduzido de 50 para 40
                doc.addPage();
                yPos = margin + 5;
            } else {
                yPos = pageHeight - 45; // Reduzido de 55 para 45
            }
            
            // Linha divisória antes da assinatura
            doc.setDrawColor(226, 232, 240);
            doc.setLineWidth(0.3); // Reduzido de 0.5 para 0.3
            doc.line(margin, yPos, pageWidth - margin, yPos);
            yPos += 8; // Reduzido de 12 para 8
            
            // Campo de assinatura
            doc.setDrawColor(203, 213, 225);
            doc.setLineWidth(0.3); // Reduzido de 0.5 para 0.3
            const signatureBoxWidth = 90;
            const signatureBoxHeight = 30; // Reduzido de 35 para 30
            doc.rect(margin, yPos, signatureBoxWidth, signatureBoxHeight, 'S');
            
            // Adicionar assinatura se existir
            if (signatureCanvasRef.current) {
                try {
                    const signatureDataUrl = signatureCanvasRef.current.toDataURL('image/png');
                    if (signatureDataUrl && signatureDataUrl !== 'data:,') {
                        const signatureWidth = signatureBoxWidth - 4;
                        const signatureHeight = signatureBoxHeight - 6; // Reduzido de 8 para 6
                        doc.addImage(signatureDataUrl, 'PNG', margin + 2, yPos + 2, signatureWidth, signatureHeight);
                    }
                } catch (error) {
                    console.error('Erro ao adicionar assinatura:', error);
                }
            }
            
            // Nome do nutricionista e CRN
            yPos += signatureBoxHeight + 4; // Reduzido de 6 para 4
            doc.setFontSize(9); // Reduzido de 10 para 9
            doc.setTextColor(15, 23, 42);
            doc.setFont('helvetica', 'normal');
            
            // Nome do nutricionista
            if (nutritionistName) {
                doc.text(nutritionistName, margin, yPos);
                yPos += 5; // Reduzido de 6 para 5
            }
            
            // CRN
            if (nutritionistCRM && nutritionistCRM.trim()) {
                doc.setFont('helvetica', 'bold');
                doc.text(`CRN: ${nutritionistCRM}`, margin, yPos);
            } else {
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(100, 116, 139);
                doc.text('Nutricionista', margin, yPos);
            }
            
            // Rodapé discreto
            const footerY = pageHeight - 10; // Reduzido de 12 para 10
            doc.setFontSize(7);
            doc.setTextColor(148, 163, 184);
            doc.setFont('helvetica', 'normal');
            doc.text('Documento gerado eletronicamente - NutriSync', pageWidth / 2, footerY, { align: 'center' });
            
            // Adicionar marca d'água em todas as páginas
            const totalPages = doc.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                addWatermark(i);
            }

            const blob = await generatePDFBlob(doc);
            setPdfBlob(blob);
            setIsPDFSendModalOpen(true);
        } catch (error) {
            console.error('Erro ao gerar PDF:', error);
            alert('Erro ao gerar PDF. Tente novamente.');
        }
    };

    const getPatientName = (id: string): string => {
        const p = pacientes.find((x: Paciente) => x.id === id);
        return p?.nome || 'Paciente';
    };

    const groupedExams = EXAM_TYPES.reduce((acc, exam) => {
        if (!acc[exam.category]) {
            acc[exam.category] = [];
        }
        acc[exam.category].push(exam);
        return acc;
    }, {} as Record<string, Array<typeof EXAM_TYPES[number]>>);

    return (
        <div className="ns-page">
            <div className="ns-page-header">
                <div>
                    <h2>Pedido de Exames</h2>
                    <p>Crie pedidos de exames personalizados para seus pacientes.</p>
                </div>
                <div className="ns-page-header-actions">
                    <Button variant="secondary" onClick={() => setShowHistory(!showHistory)}>
                        📋 Histórico
                    </Button>
                    {selectedPatient && selectedExams.length > 0 && (
                        <>
                            <Button variant="secondary" onClick={handlePrint}>🖨️ Imprimir Pedido</Button>
                            <Button onClick={handleSendPDF}>📤 Enviar PDF para Paciente</Button>
                        </>
                    )}
                </div>
            </div>

            <Card title="Selecionar Paciente">
                <label className="ns-field">
                    <span>Paciente</span>
                    <select
                        className="ns-input"
                        value={selectedPatient}
                        onChange={(e) => setSelectedPatient(e.target.value)}
                    >
                        <option value="">Selecione um paciente</option>
                        {pacientes.map((p) => (
                            <option key={p.id} value={p.id}>
                                {p.nome}
                            </option>
                        ))}
                    </select>
                </label>
            </Card>

            {showHistory && examHistory.length > 0 && (
                <Card title="Histórico de Pedidos" style={{ marginTop: '16px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '300px', overflowY: 'auto' }}>
                        {examHistory.map((item) => (
                            <div
                                key={item.id}
                                style={{
                                    padding: '12px',
                                    background: 'var(--page-surface-hover)',
                                    borderRadius: '8px',
                                    border: '1px solid #e2e8f0',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease'
                                }}
                                onClick={() => loadFromHistory(item)}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#eff6ff';
                                    e.currentTarget.style.borderColor = '#0ea5e9';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'var(--page-surface-hover)';
                                    e.currentTarget.style.borderColor = '#e2e8f0';
                                }}
                            >
                                <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '4px' }}>
                                    {item.patientName}
                                </div>
                                <div style={{ fontSize: '11px', color: 'var(--page-text-muted)', marginBottom: '4px' }}>
                                    {item.exams.length} exames selecionados
                                </div>
                                <div style={{ fontSize: '10px', color: 'var(--page-text-muted)' }}>
                                    {new Date(item.createdAt).toLocaleDateString('pt-BR')} às {new Date(item.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            <Card title="Templates de Exames" style={{ marginTop: '16px' }}>
                <label className="ns-field">
                    <span>Selecionar Template (Opcional)</span>
                    <select
                        className="ns-input"
                        value={selectedTemplate}
                        onChange={(e) => setSelectedTemplate(e.target.value)}
                    >
                        <option value="">Nenhum template</option>
                        {EXAM_TEMPLATES.map((template) => (
                            <option key={template.id} value={template.id}>
                                {template.name} - {template.description}
                            </option>
                        ))}
                    </select>
                </label>
                <p style={{ fontSize: '12px', color: 'var(--page-text-muted)', marginTop: '8px' }}>
                    Selecione um template para preencher automaticamente os exames mais comuns.
                </p>
            </Card>

            {selectedPatient && (
                <>
                    <Card title="Selecionar Exames" subtitle="Escolha os exames que deseja solicitar">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            {Object.entries(groupedExams).map(([category, exams]) => {
                                const allSelected = exams.every(e => selectedExams.includes(e.id));
                                const someSelected = exams.some(e => selectedExams.includes(e.id));

                                return (
                                    <div key={category} style={{
                                        background: 'var(--page-surface-hover)',
                                        borderRadius: '12px',
                                        padding: '16px',
                                        border: '1px solid #e2e8f0'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            marginBottom: '12px',
                                            paddingBottom: '12px',
                                            borderBottom: '1px solid #e2e8f0'
                                        }}>
                                            <input
                                                type="checkbox"
                                                checked={allSelected}
                                                onChange={() => handleCategoryToggle(category)}
                                                style={{ cursor: 'pointer' }}
                                            />
                                            <strong style={{ fontSize: '14px', color: '#0f172a' }}>
                                                {category}
                                            </strong>
                                            {someSelected && !allSelected && (
                                                <span style={{
                                                    fontSize: '11px',
                                                    color: '#0ea5e9',
                                                    background: 'rgba(14, 165, 233, 0.1)',
                                                    padding: '2px 8px',
                                                    borderRadius: '999px'
                                                }}>
                                                    {exams.filter(e => selectedExams.includes(e.id)).length} selecionado(s)
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '8px' }}>
                                            {exams.map((exam) => (
                                                <label
                                                    key={exam.id}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        padding: '8px',
                                                        background: selectedExams.includes(exam.id) ? 'rgba(14, 165, 233, 0.08)' : '#ffffff',
                                                        borderRadius: '8px',
                                                        cursor: 'pointer',
                                                        fontSize: '13px',
                                                        border: selectedExams.includes(exam.id) ? '1px solid #0ea5e9' : '1px solid #e2e8f0',
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedExams.includes(exam.id)}
                                                        onChange={() => handleExamToggle(exam.id)}
                                                        style={{ cursor: 'pointer' }}
                                                    />
                                                    <span>{exam.label}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </Card>

                    <Card title="Observações Adicionais">
                        <textarea
                            className="ns-textarea"
                            rows={4}
                            value={additionalNotes}
                            onChange={(e) => setAdditionalNotes(e.target.value)}
                            placeholder="Digite observações adicionais, orientações para o paciente ou requisitos especiais..."
                        />
                    </Card>

                    <Card title="Marca d'Água Personalizada">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                                Configure uma marca d'água personalizada que aparecerá no PDF (ex: nome do consultório, logo, etc.)
                            </p>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                                <label className="ns-field" style={{ flex: 1 }}>
                                    <span>Texto da Marca d'Água</span>
                                    <input
                                        type="text"
                                        className="ns-input"
                                        value={watermarkText}
                                        onChange={(e) => setWatermarkText(e.target.value)}
                                        placeholder="Ex: Consultório NutriSync"
                                        maxLength={50}
                                    />
                                </label>
                                <Button 
                                    variant="secondary" 
                                    onClick={() => saveWatermark(watermarkText)}
                                    disabled={!watermarkText.trim()}
                                >
                                    Salvar
                                </Button>
                            </div>
                            {watermarkText && (
                                <p style={{ fontSize: '12px', color: '#10b981', margin: 0 }}>
                                    ✓ Marca d'água salva: "{watermarkText}"
                                </p>
                            )}
                        </div>
                    </Card>

                    <Card title="Assinatura Digital">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                                Assine abaixo usando o mouse ou touchscreen
                            </p>
                            <canvas
                                ref={signatureCanvasRef}
                                width={600}
                                height={200}
                                onMouseDown={startDrawing}
                                onMouseMove={draw}
                                onMouseUp={stopDrawing}
                                onMouseLeave={stopDrawing}
                                style={{
                                    border: '2px dashed #cbd5e1',
                                    borderRadius: '8px',
                                    cursor: 'crosshair',
                                    background: '#ffffff',
                                    maxWidth: '100%'
                                }}
                            />
                            <Button variant="secondary" onClick={clearSignature} style={{ alignSelf: 'flex-start' }}>
                                Limpar Assinatura
                            </Button>
                        </div>
                    </Card>

                    {showPreview && (
                        <div className="exam-request-print">
                            <div style={{
                                maxWidth: '800px',
                                margin: '0 auto',
                                padding: '30px', // Reduzido de 40px
                                background: '#ffffff'
                            }}>
                                {/* Cabeçalho profissional */}
                                <div style={{ 
                                    textAlign: 'center', 
                                    marginBottom: '20px', // Reduzido de 30px
                                    paddingBottom: '12px', // Reduzido de 20px
                                    borderBottom: '2px solid #2a9d8f'
                                }}>
                                    <div style={{ 
                                        borderTop: '2px solid #2a9d8f',
                                        marginBottom: '6px', // Reduzido de 10px
                                        paddingTop: '6px' // Reduzido de 10px
                                    }}></div>
                                    <h1 style={{ 
                                        margin: '0 0 6px 0', // Reduzido de 10px
                                        fontSize: '18px', // Reduzido de 20px
                                        color: '#0f172a', 
                                        fontWeight: 700,
                                        letterSpacing: '0.5px'
                                    }}>
                                        PEDIDO DE EXAMES LABORATORIAIS
                                    </h1>
                                    <div style={{ 
                                        borderBottom: '2px solid #2a9d8f',
                                        marginTop: '6px' // Reduzido de 10px
                                    }}></div>
                                </div>

                                {/* Informações do paciente */}
                                <div style={{ 
                                    marginBottom: '20px', // Reduzido de 30px
                                    padding: '12px', // Reduzido de 15px
                                    background: '#f8fafc',
                                    borderRadius: '8px',
                                    border: '1px solid #e2e8f0'
                                }}>
                                    <p style={{ margin: '2px 0', fontSize: '12px', lineHeight: '1.5' }}> {/* Reduzido de 13px e 1.6 */}
                                        <strong style={{ color: '#0f172a' }}>PACIENTE:</strong> {getPatientName(selectedPatient)}
                                    </p>
                                    <p style={{ margin: '2px 0', fontSize: '12px', lineHeight: '1.5' }}>
                                        <strong style={{ color: '#0f172a' }}>DATA:</strong> {new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                    </p>
                                </div>

                                {/* Exames agrupados por categoria */}
                                <div style={{ marginBottom: '20px' }}> {/* Reduzido de 30px */}
                                    <h3 style={{ 
                                        margin: '0 0 10px 0', // Reduzido de 15px
                                        fontSize: '14px', // Reduzido de 15px
                                        color: '#2a9d8f',
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        EXAMES SOLICITADOS:
                                    </h3>
                                    <div style={{ 
                                        borderTop: '1px solid #e2e8f0',
                                        paddingTop: '8px' // Reduzido de 12px
                                    }}>
                                        {Object.entries(
                                            selectedExams.reduce((acc, examId) => {
                                            const exam = EXAM_TYPES.find(e => e.id === examId);
                                                if (exam) {
                                                    if (!acc[exam.category]) {
                                                        acc[exam.category] = [];
                                                    }
                                                    acc[exam.category].push(exam.label);
                                                }
                                                return acc;
                                            }, {} as Record<string, string[]>)
                                        ).map(([category, exams]) => (
                                            <div key={category} style={{ marginBottom: '12px' }}> {/* Reduzido de 20px */}
                                                <h4 style={{ 
                                                    margin: '0 0 5px 0', // Reduzido de 8px
                                                    fontSize: '11px', // Reduzido de 12px
                                                    color: '#64748b',
                                                    fontWeight: 600,
                                                    textTransform: 'uppercase'
                                                }}>
                                                    {category}:
                                                </h4>
                                                <ul style={{ 
                                                    margin: '0 0 0 18px', // Reduzido de 20px
                                                    padding: 0, 
                                                    lineHeight: '1.5', // Reduzido de 1.8
                                                    listStyle: 'none'
                                                }}>
                                                    {exams.map((examLabel, idx) => (
                                                        <li key={idx} style={{ 
                                                            fontSize: '12px', // Reduzido de 13px
                                                            color: '#0f172a',
                                                            marginBottom: '4px', // Reduzido de 6px
                                                            paddingLeft: '3px' // Reduzido de 5px
                                                        }}>
                                                            • {examLabel}
                                                        </li>
                                                    ))}
                                    </ul>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Observações */}
                                {additionalNotes && additionalNotes.trim() && (
                                    <div style={{ 
                                        marginBottom: '20px', // Reduzido de 30px
                                        paddingTop: '15px', // Reduzido de 20px
                                        borderTop: '1px solid #e2e8f0'
                                    }}>
                                        <h3 style={{ 
                                            margin: '0 0 8px 0', // Reduzido de 12px
                                            fontSize: '14px', // Reduzido de 15px
                                            color: '#2a9d8f',
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            OBSERVAÇÕES:
                                        </h3>
                                        <div style={{ 
                                            borderTop: '1px solid #e2e8f0',
                                            paddingTop: '8px' // Reduzido de 12px
                                        }}>
                                            <p style={{ 
                                                margin: 0, 
                                                fontSize: '12px', // Reduzido de 13px
                                                color: '#0f172a',
                                                whiteSpace: 'pre-wrap',
                                                lineHeight: '1.5' // Reduzido de 1.6
                                            }}>
                                                {additionalNotes}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Assinatura */}
                                <div style={{ 
                                    marginTop: '30px', // Reduzido de 50px
                                    paddingTop: '15px', // Reduzido de 20px
                                    borderTop: '1px solid #e2e8f0' 
                                }}>
                                    <div style={{ 
                                        display: 'inline-block', 
                                        padding: '15px', // Reduzido de 20px
                                        border: '1px solid #cbd5e1', 
                                        borderRadius: '4px',
                                        minWidth: '250px',
                                        background: '#ffffff'
                                    }}>
                                        {signatureCanvasRef.current && (
                                            <div style={{ 
                                                marginBottom: '8px', // Reduzido de 12px
                                                textAlign: 'center',
                                                minHeight: '50px', // Reduzido de 60px
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <img
                                                    src={signatureCanvasRef.current.toDataURL()}
                                                    alt="Assinatura"
                                                    style={{ 
                                                        maxWidth: '180px', // Reduzido de 200px
                                                        maxHeight: '60px', // Reduzido de 70px
                                                        objectFit: 'contain'
                                                    }}
                                                />
                                            </div>
                                        )}
                                        <div style={{ 
                                            paddingTop: '6px', // Reduzido de 8px
                                            textAlign: 'left'
                                        }}>
                                            {nutritionistName && (
                                                <p style={{ 
                                                    margin: '0 0 3px 0', // Reduzido de 4px
                                                    fontSize: '11px', // Reduzido de 12px
                                                    color: '#0f172a',
                                                    fontWeight: 500
                                                }}>
                                                    {nutritionistName}
                                                </p>
                                            )}
                                            <p style={{ 
                                                margin: 0, 
                                                fontSize: '10px', // Reduzido de 11px
                                                color: nutritionistCRM ? '#0f172a' : '#64748b',
                                                fontWeight: nutritionistCRM ? 600 : 400
                                            }}>
                                                {nutritionistCRM 
                                                    ? `CRN: ${nutritionistCRM}`
                                                    : 'Assinatura do Nutricionista'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Rodapé */}
                                <div style={{ 
                                    marginTop: '20px', // Reduzido de 30px
                                    paddingTop: '12px', // Reduzido de 15px
                                    borderTop: '1px solid #e2e8f0',
                                    textAlign: 'center'
                                }}>
                                    <p style={{ 
                                        margin: 0, 
                                        fontSize: '9px',
                                        color: '#94a3b8'
                                    }}>
                                        Documento gerado eletronicamente - NutriSync
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            <style>{`
        @media print {
          /* Ocultar todos os elementos da interface */
          .ns-topbar,
          .ns-sidebar,
          .ns-page-header,
          .ns-page-header-actions,
          .ns-card:not(.exam-request-print .ns-card),
          button,
          .ns-page > .ns-card:not(.exam-request-print),
          .ns-page > *:not(.exam-request-print),
          header,
          aside,
          nav,
          .ns-topbar-left,
          .ns-topbar-right,
          .ns-topbar-trial-badge,
          .ns-topbar-notifications,
          .ns-topbar-user,
          .ns-sidebar-logo,
          .ns-sidebar-nav,
          .ns-sidebar-link,
          .ns-page-header h2,
          .ns-page-header p,
          .ns-page-header-actions button {
            display: none !important;
            visibility: hidden !important;
          }
          
          /* Mostrar apenas o conteúdo do pedido de exame */
          .exam-request-print {
            display: block !important;
            visibility: visible !important;
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            box-shadow: none !important;
            border: none !important;
          }
          
          .exam-request-print > div {
            margin: 0 auto !important;
            padding: 40px !important;
            max-width: 800px !important;
            background: white !important;
          }
          
          /* Garantir que o body não tenha elementos visíveis */
          body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          
          /* Ocultar qualquer elemento que não seja o print */
          * {
            box-shadow: none !important;
          }
          
          /* Garantir que apenas o print seja visível */
          html, body {
            height: auto !important;
            overflow: visible !important;
          }
        }
        
        @media screen {
          .exam-request-print {
            display: none;
          }
        }
      `}</style>

            {/* Modal de Envio de PDF */}
            <PDFSendModal
                isOpen={isPDFSendModalOpen}
                onClose={() => {
                    setIsPDFSendModalOpen(false);
                    setPdfBlob(null);
                }}
                onSend={async (message, sendToChat = true) => {
                    if (!user?.uid || !selectedPatient || !pdfBlob) return;
                    try {
                        const fileName = `pedido_exames_${getPatientName(selectedPatient).replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
                        await sendPDFToPatient(
                            selectedPatient,
                            user.uid,
                            'examRequest',
                            fileName,
                            pdfBlob,
                            message,
                            sendToChat
                        );
                        alert(sendToChat 
                            ? 'PDF enviado para o paciente com sucesso!'
                            : 'PDF salvo com sucesso!');
                    } catch (error) {
                        console.error('Erro ao enviar PDF:', error);
                        alert('Erro ao enviar PDF. Tente novamente.');
                        throw error;
                    }
                }}
                pdfBlob={pdfBlob}
                fileName={`pedido_exames_${selectedPatient ? getPatientName(selectedPatient).replace(/\s+/g, '_') : 'paciente'}_${new Date().toISOString().split('T')[0]}.pdf`}
                patientName={selectedPatient ? getPatientName(selectedPatient) : 'Paciente'}
            />
        </div>
    );
};
